import { BuildingSummary } from './protocols';
import { Attachment, FileAttachment, ImageAttachment, isImageAttachment as isImageAttachmentGeneral } from './attachments';
import { ProjectStatus } from './projects';
import { WorkOrderPriority } from './workOrders';
import { truncateText } from '../utils/timelineUtils';

export enum TimelineEventType {
    // Notable events (for annual reporting)
    // Completed work orders, split by what they stem from (see WorkOrder source fields)
    WORK_ORDER_MAINTENANCE = "WORK_ORDER_MAINTENANCE",  // from a maintenance activity
    WORK_ORDER_ISSUE = "WORK_ORDER_ISSUE",              // from an issue report (felanmälan)
    WORK_ORDER_OTHER = "WORK_ORDER_OTHER",              // manually created / other
    INSPECTION_COMPLETED = "INSPECTION_COMPLETED",
    COMPONENT_REPLACED = "COMPONENT_REPLACED",
    PROJECT_END = "PROJECT_END",
    MAINTENANCE_PLAN_ACTIVATED = "MAINTENANCE_PLAN_ACTIVATED",  // derived from maintenance/active + archive
    CUSTOM = "CUSTOM"
}

/** Event types derived from completed work orders (split by source). */
export const WORK_ORDER_EVENT_TYPES: readonly TimelineEventType[] = [
    TimelineEventType.WORK_ORDER_MAINTENANCE,
    TimelineEventType.WORK_ORDER_ISSUE,
    TimelineEventType.WORK_ORDER_OTHER
];

export const isWorkOrderEventType = (eventType: TimelineEventType): boolean =>
    WORK_ORDER_EVENT_TYPES.includes(eventType);

export interface TimelineEvent {
    buildingKey: string;
    buildingName: string;
    buildingSummary: BuildingSummary;
    eventType: TimelineEventType;
    date: Date;
    displayDate: string;
    customEventId?: string;
    title?: string;
    description?: string;
    inexactDate?: boolean;
    attachments?: { [key: string]: TimelineAttachment };

    // Project fields
    projectId?: string;
    projectTitle?: string;
    projectStatus?: ProjectStatus;

    // Work order fields
    workOrderKey?: string;
    workOrderTitle?: string;
    workOrderPriority?: WorkOrderPriority;
    completionComment?: string;
    /** Completion evidence photos (after-pictures). KTD9: completion photos and
     *  "after" photos are the same field (workOrder.afterPictures). */
    afterPictures?: { [key: string]: import('./protocols').PhotoModel };

    // Inspection fields
    inspectionTemplateId?: string;
    inspectionTemplateName?: string;
    inspectionMeasurement?: { value: number; unit: string; withinRange: boolean };
    /** The check's component or place, for the detail modal. Not `componentKey`: that would change the event's ID (getEventId). */
    inspectionComponentKey?: string;
    inspectionLocation?: import('../Pages/BuildingChecks/buildingChecksUtils').BuildingCheck['location'];

    // Component fields
    componentKey?: string;
    componentDisplayName?: string;
    componentTypeKey?: string;
    componentPhotos?: { [key: string]: import('./protocols').PhotoModel };

    // Component replacement fields (for COMPONENT_REPLACED events)
    replacedComponentKey?: string;      // Key of the archived (old) component
    replacedComponentName?: string;     // Display name of the old component

    // Components linked to a CUSTOM event (optional, zero or more). Resolved from
    // the stored componentKeys against the building's structure when the event is built.
    linkedComponents?: { key: string; displayName: string }[];

    // Maintenance plan fields (MAINTENANCE_PLAN_ACTIVATED)
    maintenancePlan?: MaintenancePlanActivation;
}

/**
 * One activation of a maintenance plan, derived from the plan's own metadata.
 * Re-activations within a few days are folded in: the fields describe the last
 * activation of the run, `firstActivatedAt`/`activationCount` the whole run.
 */
export interface MaintenancePlanActivation {
    activatedAt: string;          // YYYY-MM-DD hh:mm:ss, the plan's metadata.activatedAt
    activatedBy?: string;         // UID; absent on old plans
    componentCount: number;
    activityCount: number;
    firstActivatedAt: string;     // first activation of the run; the event ID uses it
    activationCount: number;      // activations in the run, ≥ 1
    previousActivatedAt?: string; // last activation of the run before this one
    isCurrent: boolean;           // true for the plan in maintenance/active
}

// Re-export attachment types for backwards compatibility
// Note: TimelineFileAttachment.uploadedAt uses YYYY-MM-DD hh:mm:ss format for backwards compatibility
export interface TimelineFileAttachment extends Omit<FileAttachment, 'uploadedAt'> {
    uploadedAt: string; // Format: YYYY-MM-DD hh:mm:ss (for backwards compatibility)
}

export interface TimelineImageAttachment extends ImageAttachment {}

export type TimelineAttachment = TimelineImageAttachment | TimelineFileAttachment;

// Type guard for timeline attachments (wraps general type guard)
export const isImageAttachment = (attachment: TimelineAttachment): attachment is TimelineImageAttachment => {
    return isImageAttachmentGeneral(attachment);
};

export interface CustomTimelineItem {
    title?: string; // Short heading shown in the list. Required for new/edited events; may be absent on legacy items.
    description: string;
    date: string; // Format: YYYY-MM-DD hh:mm:ss
    createdBy: string;
    createdAt: string;
    inexactDate?: boolean; // If true, only year is significant
    attachments?: { [key: string]: TimelineAttachment };
    /** Keys of building components this event is linked to. Optional; omitted when none. */
    componentKeys?: string[];
}

/** Max length for the list title derived from a legacy custom event's body. */
const CUSTOM_EVENT_TITLE_FALLBACK_MAX_LENGTH = 80;

/**
 * One-line list title for a custom event. New events store an explicit `title`.
 * Legacy events without one fall back to a truncated version of the body so the
 * list still shows something meaningful.
 */
export const getCustomEventTitle = (event: TimelineEvent): string => {
    const title = event.title?.trim();
    if (title) return title;
    return truncateText(event.description || '', CUSTOM_EVENT_TITLE_FALLBACK_MAX_LENGTH);
};

/**
 * Get label for event type
 */
export const getEventTypeLabel = (eventType: TimelineEventType): string => {
    switch (eventType) {
        case TimelineEventType.WORK_ORDER_MAINTENANCE:
            return 'Ärende underhåll';
        case TimelineEventType.WORK_ORDER_ISSUE:
            return 'Ärende felanmälan';
        case TimelineEventType.WORK_ORDER_OTHER:
            return 'Ärende övrigt';
        case TimelineEventType.INSPECTION_COMPLETED:
            return 'Kontroll utförd';
        case TimelineEventType.COMPONENT_REPLACED:
            return 'Komponent utbytt';
        case TimelineEventType.PROJECT_END:
            return 'Projekt';
        case TimelineEventType.MAINTENANCE_PLAN_ACTIVATED:
            return 'Underhållsplan aktiverad';
        case TimelineEventType.CUSTOM:
            return 'Egen händelse';
        default:
            return '';
    }
};

/**
 * Get description for event
 */
export const getEventDescription = (event: TimelineEvent): string => {
    switch (event.eventType) {
        case TimelineEventType.WORK_ORDER_MAINTENANCE:
        case TimelineEventType.WORK_ORDER_ISSUE:
        case TimelineEventType.WORK_ORDER_OTHER:
            return event.workOrderTitle || '';
        case TimelineEventType.INSPECTION_COMPLETED:
            return event.inspectionTemplateName || 'Kontroll';
        case TimelineEventType.COMPONENT_REPLACED:
            return event.replacedComponentName && event.componentDisplayName
                ? `Bytte ut: ${event.replacedComponentName} → ${event.componentDisplayName}`
                : event.componentDisplayName || '';
        case TimelineEventType.PROJECT_END:
            return event.projectTitle || '';
        case TimelineEventType.MAINTENANCE_PLAN_ACTIVATED:
            return 'Underhållsplan aktiverad';
        case TimelineEventType.CUSTOM:
            // List/summary text: the title, or a truncated body for legacy items.
            return getCustomEventTitle(event);
        default:
            return '';
    }
};

/**
 * Get color for event type (for timeline markers and PDF export)
 */
export const getEventColor = (eventType: TimelineEventType): string => {
    switch (eventType) {
        case TimelineEventType.WORK_ORDER_MAINTENANCE:
            return '#465064'; // Blue-grey
        case TimelineEventType.WORK_ORDER_ISSUE:
            return '#D66F40'; // Orange
        case TimelineEventType.WORK_ORDER_OTHER:
            return '#747C8B'; // Grey-blue
        case TimelineEventType.INSPECTION_COMPLETED:
            return '#468C49'; // Green
        case TimelineEventType.COMPONENT_REPLACED:
            return '#8C78A4'; // Purple
        case TimelineEventType.PROJECT_END:
            return '#313543'; // Dark blue
        case TimelineEventType.MAINTENANCE_PLAN_ACTIVATED:
            return '#3F4B6F'; // Indigo
        case TimelineEventType.CUSTOM:
            return '#516873'; // Teal-blue
        default:
            return '#3cb6df';
    }
};
